import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getProductName, getProductDescription, getBrandName, getMarketName } from '../utils/productUtils';
import { formatNumberMax1Digit } from '../utils/numberUtils';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CloseIcon from '@mui/icons-material/Close';
import { useCart } from '../cart/CartManager';
import { useAuth } from '../auth';

import { useWishlist } from '../contexts/WishlistContext';
import ShippingMethodDialog from '../components/products/ShippingMethodDialog';
import CartConfirmationDialog from '../components/shop/CartConfirmationDialog';
import { useTranslation } from 'react-i18next';
import { Product } from '../types/product';
import { productApi } from '../api/productDetails';
import { useBrands } from '../contexts/BrandContext';
import { enhanceProductsWithBrands } from '../utils/brandUtils';
import { useMarketShippingMethods, useMarkets } from '../api/product';
import type { Market } from '../api/product';

const ProductDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const { isAuthenticated } = useAuth();
  const { brands } = useBrands();
  
  // Product can come from location state (navigation) or be fetched from API (direct URL)
  const [product, setProduct] = useState<Product | null>(location.state?.product || null);
  const [loading, setLoading] = useState(!location.state?.product);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState<{
    name: string;
    marketId: number;
    market?: Market;
  } | null>(null);
  const { addToCart } = useCart();
  const [addingToCart, setAddingToCart] = useState(false);
  const { isInWishlist, getWishlistId, addToWishlist, removeFromWishlist } = useWishlist();
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [refundsDialogOpen, setRefundsDialogOpen] = useState(false);
  const [refundsContent, setRefundsContent] = useState<{
    title: string;
    titleZh: string;
    titleCn: string;
    content: string;
    contentZh: string;
    contentCn: string;
  } | null>(null);
  const [cartConfirmationOpen, setCartConfirmationOpen] = useState(false);
  const [addedProduct, setAddedProduct] = useState<Product | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<{ [key: string]: string }>({});

  const { t, i18n } = useTranslation();

  // Get shipping methods data
  const { shippingMethodsData } = useMarketShippingMethods();
  
  // Get markets data
  const { marketsData } = useMarkets();

  // Get free shipping threshold for selected market
  const getFreeShippingThreshold = () => {
    if (!selectedMarket || !shippingMethodsData) return null;
    
    const freeShippingMethod = shippingMethodsData.find(
      (method) => method.marketId === selectedMarket.marketId && method.type === 'free_shipping'
    );
    
    return freeShippingMethod?.minAmount || null;
  };

  const freeShippingThreshold = getFreeShippingThreshold();

  useEffect(() => {
    // Only navigate back if we have no product and no productId (invalid URL)
    if (!product && !productId) {
      navigate(-1);
    }
  }, [product, productId, navigate]);

  // Fetch product details if not provided via location state
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!productId) {
        setError('Product ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const productData = await productApi.getProductDetails(parseInt(productId));
        
        // Enhance product with brand information
        const enhancedProduct = brands.length > 0 
          ? enhanceProductsWithBrands([productData], brands)[0]
          : productData;
        
        setProduct(enhancedProduct);
      } catch (err) {
        console.error('Error fetching product details:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we don't have product from location state
    if (!location.state?.product && productId) {
      fetchProductDetails();
    }
  }, [productId, location.state?.product, brands]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset selectedImage when product changes
  useEffect(() => {
    if (product) {
      setSelectedImage(0);
    }
  }, [product]);

  // Auto-select first option for each variant when product loads
  useEffect(() => {
    if (product?.variants && product.variants.length > 0) {
      const initialVariants: { [key: string]: string } = {};
      const firstVariant = product.variants[0];
      
      // For each option type, select the first available value
      Object.keys(firstVariant.options).forEach((optionKey: string) => {
        const uniqueValues = [...new Set(product.variants?.map((v: { options: { [x: string]: unknown; }; }) => v.options[optionKey]) || [])];
        if (uniqueValues.length > 0) {
          initialVariants[optionKey] = String(uniqueValues[0]);
        }
      });
      
      setSelectedVariants(initialVariants);
    }
  }, [product]);

  // If selectedImage is out of bounds, reset it
  useEffect(() => {
    if (product && selectedImage >= (product.imageGallery?.length || 1)) {
      setSelectedImage(0);
    }
  }, [selectedImage, product]);

  // Fetch Return & Exchange content
  const fetchRefundsContent = async () => {
    try {
      const response = await fetch('/api/page/posts/slug/return&exchange');
      const data = await response.json();
      setRefundsContent(data);
    } catch (error) {
      console.error('Error fetching refunds content:', error);
    }
  };

  // Fetch content when dialog opens
  useEffect(() => {
    if (refundsDialogOpen && !refundsContent) {
      fetchRefundsContent();
    }
  }, [refundsDialogOpen, refundsContent]);

  // Get content based on current language
  const getRefundsTitle = () => {
    // Always return the static title for the UI
    return t('product.refundsAndExchanges');
  };

  // Get selected variant information
  const getSelectedVariant = () => {
    if (!product?.variants || product.variants.length === 0) return null;
    
    // Find variant that matches all selected options
    return product.variants.find((variant: { options: { [x: string]: string; }; }) => {
      return Object.keys(selectedVariants).every(key => 
        variant.options[key] === selectedVariants[key]
      );
    });
  };

  // Get current price and compare price (variant or main product)
  const getCurrentPrice = () => {
    const selectedVariant = getSelectedVariant();
    if (selectedVariant) {
      return {
        price: selectedVariant.price || 0,
        comparePrice: selectedVariant.comparePrice || 0,
        inStock: selectedVariant.inventory?.inStock || false,
        available: selectedVariant.inventory?.available || 0
      };
    }
    return {
      price: product?.price || 0,
      comparePrice: product?.comparePrice || 0,
      inStock: product?.inventory?.inStock || false,
      available: product?.inventory?.available || 0
    };
  };

  // Check if all variants are selected
  const areAllVariantsSelected = () => {
    if (!product?.variants || product.variants.length === 0) return true;
    if (product.variants.length === 0) return true;
    
    const requiredOptions = Object.keys(product.variants[0].options);
    return requiredOptions.every(optionKey => selectedVariants[optionKey]);
  };

  // Handle variant selection
  const handleVariantChange = (optionKey: string, value: string) => {
    setSelectedVariants(prev => ({
      ...prev,
      [optionKey]: value
    }));
  };

  // Get dialog title based on current language
  const getRefundsDialogTitle = () => {
    if (!refundsContent) return t('product.refundsAndExchanges');
    
    switch (i18n.language) {
      case 'zh':
        return refundsContent.titleZh || refundsContent.title;
      case 'cn':
        return refundsContent.titleCn || refundsContent.title;
      default:
        return refundsContent.title;
    }
  };

  const getRefundsContent = () => {
    if (!refundsContent) return '';
    
    switch (i18n.language) {
      case 'zh':
        return refundsContent.contentZh || refundsContent.content;
      case 'cn':
        return refundsContent.contentCn || refundsContent.content;
      default:
        return refundsContent.content;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Container sx={{ maxWidth: 'lg', margin: '0 auto', p: { xs: 2, md: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: 2
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            {t('common.loading')}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Show error state
  if (error) {
    return (
      <Container sx={{ maxWidth: 'lg', margin: '0 auto', p: { xs: 2, md: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: 2
        }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            {t('common.goBack')}
          </Button>
        </Box>
      </Container>
    );
  }

  // Show nothing if no product
  if (!product) return null;

  // Ensure we have a valid gallery array
  const validImages = (product.imageGallery && product.imageGallery.length > 0) 
    ? product.imageGallery.filter((url: string) => url && typeof url === 'string' && url.trim() !== '')
    : [];
  
  const gallery = validImages.length > 0 
    ? validImages 
    : (product.imageUrl && typeof product.imageUrl === 'string' && product.imageUrl.trim() !== '') 
      ? [product.imageUrl] 
      : ['/images/img_placeholder.svg'];
  
  // Ensure selectedImage is within bounds
  const safeSelectedImage = Math.min(Math.max(0, selectedImage), gallery.length - 1);
  
  const handleOpenShippingDialog = () => setShippingDialogOpen(true);
  const handleCloseShippingDialog = () => setShippingDialogOpen(false);
  const handleSelectMarket = (marketId: number, name: string) => {
    const market = marketsData.find((m: Market) => m.id === marketId);
    setSelectedMarket({ marketId, name, market });
    setShippingDialogOpen(false);
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      navigate('/sign-in');
      return;
    }

    if (!product) return;

    setWishlistLoading(true);
    try {
      if (isInWishlist(product.id)) {
        // Remove from wishlist using wishlist ID
        const wishlistId = getWishlistId(product.id);
        if (wishlistId) {
          await removeFromWishlist(wishlistId);
        }
      } else {
        // Add to wishlist
        await addToWishlist(product.id);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    } finally {
      setWishlistLoading(false);
    }
  };

  return (
    <Container sx={{ maxWidth: 'lg', margin: '0 auto', p: { xs: 2, md: 3 } }}>
      {/* Product Images */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 3, md: 8 }
      }}>
        <Box sx={{ flex: { xs: 'none', md: 2 } }}>
          {/* Main Preview Image */}
          <Box
            sx={{
              width: { xs: '100%', md: '588px' },
              height: { xs: '300px', md: '588px' },
              borderRadius: 2,
              marginBottom: 4,
              backgroundImage: `url(${gallery[safeSelectedImage]})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              position: 'relative',
            }}
            onError={(e) => {
              console.error('Failed to load image:', gallery[safeSelectedImage]);
              // Fallback to placeholder
              e.currentTarget.style.backgroundImage = 'url(/images/img_placeholder.svg)';
            }}
          />
          {/* Thumbnails */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              width: '100%',
              overflowX: 'auto',
              paddingBottom: 4,
              margin: '0 auto',
            }}
          >
            {gallery.map((img: string, idx: number) => (
              <img
                key={`${img}-${idx}`}
                src={img}
                alt={`${product.name} thumbnail ${idx + 1}`}
                onError={(e) => {
                  console.error('Failed to load thumbnail:', img);
                  e.currentTarget.src = '/images/img_placeholder.svg';
                }}
                style={{
                  width: 70,
                  height: 70,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border:
                    safeSelectedImage === idx ? '1px solid #222' : '1px solid #ccc',
                  cursor: 'pointer',
                  background: '#fff',
                  flexShrink: 0,
                }}
                onClick={() => setSelectedImage(idx)}
              />
            ))}
          </div>
        </Box>

        {/* Product Info */}
        <Box sx={{ flex: { xs: 'none', md: 2 } }}>
          <div style={{ marginBottom: 8 }}>
            <span
              style={{
                background: '#e0f7e9',
                color: '#388e3c',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              BEST
            </span>
          </div>
          <Box
            onClick={() => {
              if (product.brandId) {
                navigate(`/brand/${product.brandId}/${encodeURIComponent(product.brand || 'brand')}`);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: product.brandId ? 'pointer' : 'default',
              margin: '8px 0',
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (product.brandId) {
                e.currentTarget.style.opacity = '0.7';
              }
            }}
            onMouseLeave={(e) => {
              if (product.brandId) {
                e.currentTarget.style.opacity = '1';
              }
            }}
          >
            <Typography
              style={{
                fontSize: 28,
                color: '#17110D',
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              {getBrandName(product, i18n.language) || product.vendor || t('product.brand')}
            </Typography>

          </Box>
          <Typography
            style={{
              fontSize: 20,
              margin: '8px 0',
              color: '#3D3C3C',
              fontWeight: 600,
            }}
          >
            {getProductName(product, i18n.language)}
          </Typography>
          {/* Price and discount */}
          <Box style={{ margin: '16px 0' }}>
            {/* Only show price if all variants are selected or if product has no variants */}
            {areAllVariantsSelected() ? (
              <>
                {product.comparePrice && product.comparePrice > product.price && (
                  <>
                    {/* First line: compare price with info icon */}
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          textDecoration: 'line-through',
                          color: '#99A1A8',
                          fontSize: 16,
                        }}
                      >
                        HK${getCurrentPrice().comparePrice?.toFixed(2)}
                      </span>
                      <span
                        title={t('product.originalPriceBeforeDiscount')}
                        style={{ fontSize: 16, color: '#888', cursor: 'pointer' }}
                      >
                        <svg
                          width="16"
                          height="16"
                          style={{ verticalAlign: 'middle' }}
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="#888"
                            strokeWidth="2"
                          />
                          <text
                            x="12"
                            y="16"
                            textAnchor="middle"
                            fontSize="12"
                            fill="#888"
                          >
                            i
                          </text>
                        </svg>
                      </span>
                    </Box>
                    {/* Second line: discount percent and price */}
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginTop: 2,
                      }}
                    >
                      <span
                        style={{ color: '#B31818', fontWeight: 700, fontSize: 24 }}
                      >
                        {/* TODO: API should provide discountPercentage instead of frontend calculation */}
                        {(() => {
                          const currentPrice = getCurrentPrice();
                          if (currentPrice.comparePrice && currentPrice.comparePrice > currentPrice.price) {
                            return `-${Math.floor(((currentPrice.comparePrice - currentPrice.price) / currentPrice.comparePrice) * 100)}%`;
                          }
                          return '';
                        })()}
                      </span>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{ color: '#17110D', fontWeight: 700, fontSize: 24 }}
                        >
                          HK${getCurrentPrice().price.toFixed(2)}
                        </span>
                        {/* Sold Out Chip - only show when out of stock */}
                        {!getCurrentPrice().inStock && (
                          <Box
                            style={{
                              backgroundColor: '#FFEBEE',
                              color: '#C62828',
                              padding: '3px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                              border: '1px solid #FFCDD2',
                            }}
                          >
                            Sold Out
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </>
                )}
                {(() => {
                  const currentPrice = getCurrentPrice();
                  if (!(currentPrice.comparePrice && currentPrice.comparePrice > currentPrice.price)) {
                    return (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ color: '#d32f2f', fontWeight: 700, fontSize: 28 }}>
                          HK${currentPrice.price.toFixed(2)}
                        </span>
                        {/* Sold Out Chip - only show when out of stock */}
                        {!currentPrice.inStock && (
                          <Box
                            style={{
                              backgroundColor: '#FFEBEE',
                              color: '#C62828',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                              border: '1px solid #FFCDD2',
                            }}
                          >
                            Sold Out
                          </Box>
                        )}
                      </Box>
                    );
                  }
                  return null;
                })()}
              </>
            ) : (
              /* Show message when variants need to be selected */
              <Box style={{ 
                padding: '12px 16px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                border: '1px solid #e9ecef',
                marginTop: '8px'
              }}>
                <Typography style={{ 
                  fontSize: '14px', 
                  color: '#666', 
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}>
                  {t('product.selectOptionsToSeePrice')}
                </Typography>
              </Box>
            )}
          </Box>
        
          {/* Variant Selection Dropdowns */}
          {product.variants && product.variants.length > 0 && (
            <Box style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {Object.keys(product.variants[0].options).map((optionKey: string) => {
                  const uniqueValues = [...new Set(product.variants?.map((v: { options: { [x: string]: unknown; }; }) => v.options[optionKey]) || [])];
                  return (
                    <div key={optionKey} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{optionKey}:</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {uniqueValues.map((value: unknown) => {
                          const isSelected = selectedVariants[optionKey] === String(value);
                          return (
                            <button
                              key={String(value)}
                              onClick={() => handleVariantChange(optionKey, String(value))}
                              style={{
                                padding: '8px 16px',
                                border: `2px solid ${isSelected ? '#b48b6a' : '#ddd'}`,
                                borderRadius: '20px',
                                fontSize: '14px',
                                backgroundColor: isSelected ? '#b48b6a' : 'white',
                                color: isSelected ? 'white' : '#333',
                                cursor: 'pointer',
                                fontWeight: isSelected ? '600' : '400',
                                transition: 'all 0.2s ease-in-out',
                                minWidth: '80px',
                                textAlign: 'center',
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = '#b48b6a';
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = '#ddd';
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              {String(value)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Box>
          )}
        
          {/* Deliver to Hongkong */}
          <Box
            style={{
              margin: '24px 0',
              borderTop: '1px solid #eee',
              borderBottom: '1px solid #eee',
              padding: '16px 0',
              cursor: 'pointer',
            }}
            onClick={handleOpenShippingDialog}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{
                  color: '#757D86',
                  fontWeight: 700,
                  fontSize: 16,
                  marginBottom: 4,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <span style={{ marginRight: 8 }}>
                  <img
                    src={'/icons/ic_shipping.svg'}
                    alt="plane"
                    style={{ width: 18, height: 18 }}
                  />
                </span>
                {t('product.deliverTo')} {selectedMarket?.market ? getMarketName(selectedMarket.market, i18n.language) : selectedMarket?.name || ''}
              </div>
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path
                  d="M9 6l6 6-6 6"
                  stroke="#99A1A8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div style={{ color: '#757D86', fontSize: 14 }}>
              {freeShippingThreshold ? (
                <>
                  {t('product.freeShipping')} {t('product.over')} HK${formatNumberMax1Digit(freeShippingThreshold)}
                </>
              ) : null}
            </div>
          </Box>
          {/* Quantity selector and Add to Cart button */}
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              margin: '32px 0 24px 0',
            }}
          >
            {/* Quantity Selector */}
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: 8,
                overflow: 'hidden',
                height: 36,
              }}
            >
              <button
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                disabled={quantity === 1}
                style={{
                  width: 36,
                  height: 36,
                  border: 'none',
                  background: 'none',
                  fontSize: 24,
                  borderRight: '1px solid #E5E7EA',
                  color: quantity === 1 ? '#ccc' : '#222',
                  cursor: quantity === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                −
              </button>
              <span style={{ width: 40, textAlign: 'center', fontSize: 18 }}>
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(q => q + 1)}
                style={{
                  width: 36,
                  height: 36,
                  border: 'none',
                  background: 'none',
                  fontSize: 24,
                  color: '#222',
                  borderLeft: '1px solid #E5E7EA',
                  cursor: 'pointer',
                }}
              >
                +
              </button>
            </Box>
            {/* Selling Fast */}
            <span
              style={{
                fontSize: 13,
                color: '#131518',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {t('product.sellingFast')}
              <svg
                width="20"
                height="20"
                style={{ marginLeft: 2 }}
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" stroke="#888" strokeWidth="2" />
                <text
                  x="12"
                  y="16"
                  textAnchor="middle"
                  fontSize="12"
                  fill="#888"
                >
                  i
                </text>
              </svg>
            </span>
          </Box>



          {/* Add to Cart and Favorite */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              style={{
                flex: 1,
                background: (areAllVariantsSelected() && getCurrentPrice().inStock) ? '#b48b6a' : '#ccc',
                color: '#fff',
                padding: '22px 0',
                height: 64,
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 700,
                cursor: (addingToCart || !areAllVariantsSelected() || !getCurrentPrice().inStock) ? 'not-allowed' : 'pointer',
                opacity: (addingToCart || !areAllVariantsSelected() || !getCurrentPrice().inStock) ? 0.7 : 1,
              }}
              disabled={addingToCart || !areAllVariantsSelected() || !getCurrentPrice().inStock}
              onClick={async () => {
                setAddingToCart(true);
                try {
                  // Add to cart and check if successful
                  const currentPrice = getCurrentPrice();
                  const selectedVariant = getSelectedVariant();
                  const success = await addToCart({
                    id: product.id,
                    name: product.name,
                    price: currentPrice.price,
                    imageUrl: product.imageUrl,
                    quantity,
                    variantId: selectedVariant?.id,
                    selectedOptions: selectedVariant ? selectedVariant.options : undefined,
                    maxAvailable: currentPrice.available,
                    inStock: currentPrice.inStock,
                    inventory: {
                      available: currentPrice.available,
                      inStock: currentPrice.inStock,
                      locations: []
                    }
                  });

                  // Only show cart confirmation dialog if successful
                  if (success) {
                    setAddedProduct({
                      id: product.id,
                      name: product.name,
                      brand: product.brand,
                      vendor: product.vendor,
                      imageUrl: product.imageUrl,
                      price: currentPrice.price,
                      quantity,
                      description: product.description,
                      imageGallery: product.imageGallery,
                      comparePrice: currentPrice.comparePrice,
                      nameZh: product.nameZh,
                      nameCn: product.nameCn,
                      descriptionZh: product.descriptionZh,
                      descriptionCn: product.descriptionCn,
                      brandInfo: product.brandInfo,
                      categories: product.categories,
                      trackQuantity: product.trackQuantity,
                      chargeTax: product.chargeTax,
                      continueSelling: product.continueSelling,
                      type: product.type,
                      brandId: product.brandId,
                      collections: product.collections,
                      tags: product.tags,
                      variantOptions: product.variantOptions,
                      variants: product.variants,
                      isPhysicalProduct: product.isPhysicalProduct,
                      weight: product.weight,
                      weightUnit: product.weightUnit,
                      status: product.status,
                      onlineStore: product.onlineStore,
                      pointOfSale: product.pointOfSale,
                      ourPick: product.ourPick,
                      eat365ProductId: product.eat365ProductId,
                      createdAt: product.createdAt,
                      category: product.category,
                      inStock: currentPrice.inStock,
                      inventory: product.inventory, // Keep original inventory for reference
                      sku: product.sku,
                      barcode: product.barcode,
                      costPerItem: product.costPerItem
                    });
                    setCartConfirmationOpen(true);
                  }
                } finally {
                  setAddingToCart(false);
                }
              }}
            >
              {addingToCart ? t('product.adding') : 
               !areAllVariantsSelected() ? t('product.selectOptions') : 
               `${t('product.addToCart')} - HK$${getCurrentPrice().price.toFixed(2)}`}
            </button>
            <IconButton 
              onClick={handleWishlistToggle}
              disabled={wishlistLoading}
              sx={{ 
                opacity: wishlistLoading ? 0.7 : 1,
                cursor: wishlistLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isInWishlist(product.id) ? (
                <FavoriteIcon style={{ fontSize: 32, color: '#b48b6a' }} />
              ) : (
                <FavoriteBorderIcon style={{ fontSize: 32, color: '#131518' }} />
              )}
            </IconButton>
          </Box>
          {/* Expandable sections for info, refunds, etc. */}
          <Box
            style={{
              marginTop: 32,
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
              fontSize: 16,
              lineHeight: '19px',
              letterSpacing: 0,
              color: '#131518',
            }}
          >
            {/* Row 2 */}
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 0',
                borderBottom: '1px solid #F1F1F1',
                verticalAlign: 'middle',
                cursor: 'pointer',
              }}
              onClick={() => setRefundsDialogOpen(true)}
            >
              <span>{getRefundsTitle()}</span>
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path
                  d="M9 6l6 6-6 6"
                  stroke="#99A1A8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Product Info Section - Moved to bottom of page */}
      <Box
        style={{
          marginTop: 48,
          marginBottom: 32,
          fontFamily: 'Roboto, sans-serif',
        }}
      >
        <Typography
          style={{
            fontWeight: 700,
            fontSize: 18,
            marginBottom: 16,
            color: '#131518',
          }}
        >
          {t('product.productInfo')}
        </Typography>
        <Typography
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            color: '#666',
            maxWidth: '100%',
            wordWrap: 'break-word',
          }}
          sx={{
            '@media (min-width: 768px)': {
              maxWidth: '540px',
            },
            '@media (max-width: 767px)': {
              maxWidth: '100%',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              display: 'block',
              margin: '8px 0',
            },
            '& p': {
              margin: '8px 0',
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              margin: '12px 0 8px 0',
              fontWeight: 600,
              fontSize: '14px',
            },
            '& ul, & ol': {
              margin: '8px 0',
              paddingLeft: '20px',
            },
            '& li': {
              margin: '4px 0',
            },
          }}
          dangerouslySetInnerHTML={{ __html: getProductDescription(product, i18n.language) || '' }}
        />
      </Box>

      {/* Shipping Method Dialog */}
      <ShippingMethodDialog
        open={shippingDialogOpen}
        onClose={handleCloseShippingDialog}
        selectedMarket={selectedMarket}
        onSelectMarket={handleSelectMarket}
      />

      {/* Refunds & Exchanges Dialog */}
      <Dialog
        open={refundsDialogOpen}
        onClose={() => setRefundsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            pb: 2
          }}
        >
          {getRefundsDialogTitle()}
          <IconButton
            onClick={() => setRefundsDialogOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {refundsContent ? (
            <Box
              dangerouslySetInnerHTML={{ __html: getRefundsContent() }}
              sx={{
                '& p': {
                  marginBottom: 2,
                  lineHeight: 1.6,
                  fontSize: '14px',
                  color: '#333',
                },
                '& strong': {
                  fontWeight: 600,
                },
                '& ul': {
                  paddingLeft: 3,
                  marginBottom: 2,
                },
                '& li': {
                  marginBottom: 1,
                  lineHeight: 1.6,
                },
              }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>{t('common.loading')}</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart Confirmation Dialog */}
      <CartConfirmationDialog
        open={cartConfirmationOpen}
        onClose={() => setCartConfirmationOpen(false)}
        product={addedProduct}
      />
    </Container>
  );
};

export default ProductDetails;
